import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../UserContext";
import axios from "axios";
import { FaPlus, FaExclamationTriangle } from "react-icons/fa";
import toast from "react-hot-toast";
import "./Problems.css";

const ProblemsList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { userData, token } = useContext(UserContext);

  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("notStarted");

  // This variable will help us conditionally render the layout direction
  const isRTL = i18n.language === "ar";

  useEffect(() => {
    const fetchProblems = async () => {
      if (!userData?.Farm?.id || !token) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const response = await axios.get(
          `api/problem/findByFarmId/${userData.Farm.id}`,
          {
            headers: { Authorization: token },
            params: { state: activeTab },
          }
        );
        setProblems(response.data.items || []);
      } catch (error) {
        console.error("Failed to fetch problems:", error);
        toast.error(t("Could not load problems."));
      } finally {
        setLoading(false);
      }
    };

    fetchProblems();
  }, [userData, token, activeTab, t]);

  const tabs = [
    { key: "notStarted", label: t("Current") },
    { key: "inProgress", label: t("In Progress") },
    { key: "finished", label: t("Finished") },
  ];

  const statusClassMap = {
    finished: "status-finished",
    notStarted: "status-not-started",
    inProgress: "status-in-progress",
  };

  const statusTextMap = {
    notStarted: t("Not Started"),
    inProgress: t("In Progress"),
    finished: t("Finished"),
  };

  return (
    <div className="problems-list-page">
      <header className="view-header">
        <h1 className="view-header-title">{t("Problems")}</h1>
        <button
          className="button-primary"
          onClick={() => navigate("/problems/new")}
        >
          <FaPlus /> {t("Add New Problem")}
        </button>
      </header>

      <div className="tabs-container">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`tab-button ${activeTab === tab.key ? "active" : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="problems-list-content">
        {loading ? (
          <div>{t("Loading...")}</div>
        ) : problems.length > 0 ? (
          problems.map(({ problem, field, employee }) => (
            <div
              key={problem.id}
              className="problem-item-card"
              onClick={() => navigate(`/problems/${problem.id}`)}
            >
              <div className="problem-item-top">
                {isRTL ? (
                  <>
                    <div className="problem-item-title-group">
                      <FaExclamationTriangle className="problem-icon" />
                      <h4>{problem.name}</h4>
                    </div>
                    <span
                      className={`status-badge ${
                        statusClassMap[problem.state]
                      }`}
                    >
                      {statusTextMap[problem.state]}
                    </span>
                  </>
                ) : (
                  <>
                    <div className="problem-item-title-group">
                      <FaExclamationTriangle className="problem-icon" />
                      <h4>{problem.name}</h4>
                    </div>
                    <span
                      className={`status-badge ${
                        statusClassMap[problem.state]
                      }`}
                    >
                      {statusTextMap[problem.state]}
                    </span>
                  </>
                )}
              </div>

              <div className="problem-item-main-content">
                <p className="problem-item-description">{problem.textDesc}</p>
                {field && (
                  <p className="problem-item-field-name">{field.name}</p>
                )}
              </div>

              <div className="problem-item-footer">
                {isRTL ? (
                  <>
                    <span className="problem-item-date">
                      {new Date(problem.problemDate).toLocaleDateString(
                        "ar-EG",
                        { day: "numeric", month: "long", year: "numeric" }
                      )}
                    </span>
                    {employee && (
                      <span className="problem-item-tag">
                        {employee.username}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <span className="problem-item-date">
                      {new Date(problem.problemDate).toLocaleDateString(
                        "en-US",
                        { day: "numeric", month: "long", year: "numeric" }
                      )}
                    </span>
                    {employee && (
                      <span className="problem-item-tag">
                        {employee.username}
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="no-data-message">{t("No problems found.")}</div>
        )}
      </div>
    </div>
  );
};

export default ProblemsList;
